/**
 * Autoplays background videos only when the user hasn't asked for less.
 *
 * Videos opt in with `data-autoplay` (instead of the native `autoplay`
 * attribute) and should ship with `preload="none"`, so nothing is downloaded
 * until the video is near the viewport and we've decided it's OK to play.
 * Videos pause again when they scroll out of view.
 *
 * Skipped when:
 * - prefers-reduced-motion: reduce
 * - Save-Data is on (navigator.connection.saveData, Chromium)
 * - the connection is 2g or slower (navigator.connection.effectiveType, Chromium)
 * - prefers-reduced-data: reduce (experimental media query)
 */
const SELECTOR = "video[data-autoplay]";
const SLOW_CONNECTIONS = ["slow-2g", "2g"];

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
const reducedData = window.matchMedia("(prefers-reduced-data: reduce)");
const connection = navigator.connection;

const visible = new Set();

export function canAutoplay() {
  if (reducedMotion.matches) return false;
  if (reducedData.matches) return false;
  if (connection?.saveData) return false;
  if (SLOW_CONNECTIONS.includes(connection?.effectiveType)) return false;
  return true;
}

function play(video) {
  video.preload = "auto";
  video.muted = true; // required by browsers for autoplay
  video.play().catch(() => {});
}

function apply(video) {
  if (visible.has(video) && canAutoplay()) play(video);
  else video.pause();
}

export function initAutoplayVideos() {
  // Start slightly before the video enters the viewport, pause once it leaves
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) visible.add(target);
        else visible.delete(target);
        apply(target);
      });
    },
    { rootMargin: "200px 0px" },
  );

  document
    .querySelectorAll(SELECTOR)
    .forEach((video) => observer.observe(video));

  // Pick up videos rendered later (e.g. Alpine x-if / x-for templates)
  new MutationObserver((mutations) => {
    for (const { addedNodes, removedNodes } of mutations) {
      addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches(SELECTOR)) observer.observe(node);
        node
          .querySelectorAll(SELECTOR)
          .forEach((video) => observer.observe(video));
      });
      removedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const videos = node.matches(SELECTOR)
          ? [node]
          : node.querySelectorAll(SELECTOR);
        videos.forEach((video) => {
          observer.unobserve(video);
          visible.delete(video);
        });
      });
    }
  }).observe(document.body, { childList: true, subtree: true });

  // React if the user's settings or connection change while on the page
  const update = () => document.querySelectorAll(SELECTOR).forEach(apply);
  reducedMotion.addEventListener("change", update);
  reducedData.addEventListener("change", update);
  connection?.addEventListener?.("change", update);
}
